/* ============================================================
   THE SLEEP CO. | BUYING GUIDE
   Size comparator, chapter scroll spy, reveals, engagement signals.
   Safe to load with defer. Re-initialises inside the theme editor.
   ============================================================ */
(function () {
  "use strict";

  var SIZES = {
    single:      { name: "Single",       w: 90,  l: 190, shared: false },
    smalldouble: { name: "Small Double", w: 120, l: 190, shared: true },
    double:      { name: "Double",       w: 135, l: 190, shared: true },
    king:        { name: "King",         w: 150, l: 200, shared: true },
    euroking:    { name: "Euro King",    w: 160, l: 200, shared: true },
    superking:   { name: "Super King",   w: 180, l: 200, shared: true },
    emperor:     { name: "Emperor",      w: 200, l: 200, shared: true }
  };

  var REF = 210;             /* the stage represents a 210cm square */
  var BASE = SIZES.double;   /* the reference size held behind the selection */

  function tidy(n) { return (Math.round(n * 10) / 10).toString(); }
  function pct(cm) { return (cm / REF * 100) + "%"; }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  /* ---------- Size comparator ---------- */
  function initSizer(root) {
    if (!root || root.dataset.tscReady === "1") return;
    root.dataset.tscReady = "1";

    var bed    = root.querySelector("[data-tsc-bed]");
    var ghost  = root.querySelector("[data-tsc-ghost]");
    var split  = root.querySelector("[data-tsc-split]");
    var halfL  = root.querySelector("[data-tsc-half='l']");
    var halfR  = root.querySelector("[data-tsc-half='r']");
    var oName  = root.querySelector("[data-tsc-out='name']");
    var oDims  = root.querySelector("[data-tsc-out='dims']");
    var oPer   = root.querySelector("[data-tsc-out='per']");
    var oVerd  = root.querySelector("[data-tsc-out='verdict']");
    var tabs   = Array.prototype.slice.call(root.querySelectorAll("[data-tsc-size]"));

    if (!bed || !tabs.length) return;

    function render(key) {
      var s = SIZES[key];
      if (!s) return;

      bed.style.width  = pct(s.w);
      bed.style.height = pct(s.l);

      var bigger = s.w > BASE.w;
      if (ghost) {
        ghost.setAttribute("data-visible", bigger ? "true" : "false");
        ghost.style.width  = pct(BASE.w);
        ghost.style.height = pct(BASE.l);
      }

      var per = s.w / 2;
      var showSplit = s.shared ? "true" : "false";
      if (split) split.setAttribute("data-visible", showSplit);
      if (halfL) { halfL.setAttribute("data-visible", showSplit); halfL.textContent = s.shared ? tidy(per) + "cm" : ""; }
      if (halfR) { halfR.setAttribute("data-visible", showSplit); halfR.textContent = s.shared ? tidy(per) + "cm" : ""; }

      if (oName) oName.textContent = s.name;
      if (oDims) oDims.textContent = s.w + " x " + s.l + "cm";
      if (oPer)  oPer.textContent  = s.shared ? tidy(per) + "cm" : "Not shared";

      if (oVerd) {
        var v;
        if (!s.shared) {
          v = "A Single is 90cm wide. Useful as a reference point: in a Double, each person gets less width than this.";
        } else if (s.w < BASE.w) {
          v = "Each sleeper gets <b>" + esc(tidy(per)) + "cm</b>. That is well under the 90cm of a single bed. Comfortable for one, tight for two.";
        } else if (s.w === BASE.w) {
          v = "Each sleeper gets <b>67.5cm</b>. A single bed is 90cm wide, so two adults in a Double each have less room than a child in a single.";
        } else {
          var gain = tidy(per - (BASE.w / 2));
          var lenNote = s.l > BASE.l ? " It is also 10cm longer, which matters if either of you is over six foot." : "";
          v = "Each sleeper gets <b>" + esc(tidy(per)) + "cm</b>, which is <b>" + esc(gain) + "cm more each</b> than a Double." + lenNote;
        }
        oVerd.innerHTML = v;
      }

      tabs.forEach(function (t) {
        t.setAttribute("aria-pressed", t.getAttribute("data-tsc-size") === key ? "true" : "false");
      });
    }

    tabs.forEach(function (t) {
      t.addEventListener("click", function () {
        var k = t.getAttribute("data-tsc-size");
        render(k);
        signal("GuideSizeToolUsed", { size: k });
      });
    });

    var initial = root.getAttribute("data-tsc-default") || "double";
    render(SIZES[initial] ? initial : "double");
  }

  /* ---------- Chapter scroll spy ---------- */
  function initRail(root) {
    if (!root || root.dataset.tscRailReady === "1") return;
    root.dataset.tscRailReady = "1";

    var links = Array.prototype.slice.call(root.querySelectorAll("[data-tsc-rail-link]"));
    var chapters = Array.prototype.slice.call(root.querySelectorAll("[data-tsc-chapter]"));
    if (!links.length || !chapters.length || !("IntersectionObserver" in window)) return;

    var spy = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        links.forEach(function (l) {
          l.setAttribute("aria-current", l.getAttribute("href") === "#" + e.target.id ? "true" : "false");
        });
      });
    }, { rootMargin: "-15% 0px -70% 0px", threshold: 0 });

    chapters.forEach(function (c) { spy.observe(c); });
  }

  /* ---------- Reveal on scroll ---------- */
  function initReveals(scope) {
    var els = Array.prototype.slice.call((scope || document).querySelectorAll(".tsc-g-reveal:not(.is-in)"));
    if (!els.length) return;

    if (!("IntersectionObserver" in window)) {
      els.forEach(function (el) { el.classList.add("is-in"); });
      return;
    }

    var ro = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add("is-in"); obs.unobserve(e.target); }
      });
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.06 });

    els.forEach(function (el) { ro.observe(el); });
  }

  /* ---------- Engagement signals ----------
     Custom events only. These deliberately avoid standard Meta events
     so the guide never contaminates purchase or lead reporting. ---- */
  var fired = {};
  function signal(name, payload) {
    if (fired[name]) return;
    fired[name] = true;
    try {
      if (typeof window.fbq === "function") { window.fbq("trackCustom", name, payload || {}); }
      if (window.dataLayer && typeof window.dataLayer.push === "function") {
        window.dataLayer.push(Object.assign({ event: name }, payload || {}));
      }
    } catch (err) { /* analytics must never break the page */ }
  }

  function initSignals(root) {
    if (!root || root.dataset.tscSignalsReady === "1") return;
    root.dataset.tscSignalsReady = "1";

    var deep = root.querySelector("[data-tsc-deep-marker]");
    if (deep && "IntersectionObserver" in window) {
      var o = new IntersectionObserver(function (entries, obs) {
        if (entries[0].isIntersecting) { signal("GuideReadDeep"); obs.disconnect(); }
      }, { threshold: 0.2 });
      o.observe(deep);
    }
    window.setTimeout(function () { signal("GuideEngaged60s"); }, 60000);
  }

  /* ---------- Boot ---------- */
  function boot(scope) {
    var s = scope || document;
    Array.prototype.slice.call(s.querySelectorAll("[data-tsc-sizer]")).forEach(initSizer);
    Array.prototype.slice.call(s.querySelectorAll("[data-tsc-guide]")).forEach(function (g) {
      initRail(g);
      initSignals(g);
    });
    initReveals(s);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { boot(document); });
  } else {
    boot(document);
  }

  /* Theme editor lifecycle */
  document.addEventListener("shopify:section:load", function (e) { boot(e.target); });
  document.addEventListener("shopify:block:select", function (e) {
    if (e.target && e.target.scrollIntoView) { e.target.scrollIntoView({ block: "center" }); }
  });
})();
